import { useEffect, useState } from "react";

/**
 * 교사 첫 화면 안내 팝업. 레벨 체험과 문제뱅크 오픈(beta) 두 가지를
 * **들어올 때마다 하나씩 번갈아** 띄운다 (2026-08-24).
 *
 * 지인에듀 메인의 하단 팝업과 같은 방식으로, '오늘 하루 보지 않기'를 누르면
 * 그날이 지날 때까지 다시 띄우지 않는다. 상태는 localStorage 에만 둔다.
 */
const HIDDEN_UNTIL_KEY = "experience_promo_hidden_until";
const ROTATION_KEY = "promo_rotation_count";
const GUIDE_URL = "/teacher/review-guide.html";
const PROBLEM_BANK_PATH = "/worksheet/bank";

const todayString = () => new Date().toISOString().slice(0, 10);

const PROMO_LIST = [
  {
    key: "leveltest-guide",
    eyebrow: "ECHO LEVEL TEST",
    title: (
      <>
        신입생 상담이 쉬워지고,
        <br />
        재원생은 오래 다니게
      </>
    ),
    description: (
      <>
        수능까지 남은 여정을 숫자로 보여주는 레벨테스트.
        <br />
        무엇이 다른지 한 페이지로 보여드립니다.
      </>
    ),
    buttonText: "비법의 레벨테스트 살펴보기",
    // 누가 봤는지 알 수 있게 아이디를 실어 보낸다. 페이지가 열리면
    // 서버가 텔레그램으로 알린다.
    onButtonClick: (nickname) =>
      window.open(
        `${GUIDE_URL}?src=promo&who=${encodeURIComponent(nickname || "")}`,
        "_blank",
        "noopener"
      ),
  },
  {
    key: "problem-bank",
    eyebrow: "PROBLEM BANK ✨ beta",
    title: (
      <>
        문제뱅크 오픈!
        <br />
        골라 뽑아 바로 출제하세요
      </>
    ),
    description: (
      <>
        범위를 골라 시험지를 만들면 보통 과제처럼 출제되고,
        <br />
        틀린 문항은 무한복습으로 이어져요. 체험 계정으로 먼저 확인해 보세요.
      </>
    ),
    buttonText: "문제뱅크 구경하기",
    onButtonClick: () => window.location.assign(PROBLEM_BANK_PATH),
  },
];

const ExperiencePromoModal = ({ nickname = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [promo, setPromo] = useState(PROMO_LIST[0]);

  useEffect(() => {
    let rotation = 0;
    try {
      const hiddenUntil = localStorage.getItem(HIDDEN_UNTIL_KEY);
      if (hiddenUntil && hiddenUntil >= todayString()) return;
      rotation = parseInt(localStorage.getItem(ROTATION_KEY) || "0", 10) || 0;
      localStorage.setItem(ROTATION_KEY, String(rotation + 1));
    } catch (error) {
      /* localStorage 를 못 쓰는 환경이면 그냥 첫 팝업을 띄운다 */
    }
    setPromo(PROMO_LIST[rotation % PROMO_LIST.length]);
    setIsOpen(true);
  }, []);

  if (!isOpen) return null;

  const close = () => setIsOpen(false);
  const hideToday = () => {
    try {
      localStorage.setItem(HIDDEN_UNTIL_KEY, todayString());
    } catch (error) {
      /* 저장 실패는 무시한다. 닫히기만 하면 된다 */
    }
    setIsOpen(false);
  };

  return (
    <div
      onClick={close}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14, 20, 27, 0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "460px",
          background: "#fff",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(14, 20, 27, 0.3)",
        }}
      >
        <div style={{ padding: "30px 28px 24px", background: "#EFFBF8" }}>
          <p
            style={{
              fontSize: "0.76rem",
              fontWeight: 700,
              letterSpacing: "0.22em",
              color: "#00A396",
              margin: 0,
            }}
          >
            {promo.eyebrow}
          </p>
          <h2
            style={{
              fontSize: "1.35rem",
              fontWeight: 900,
              lineHeight: 1.4,
              margin: "10px 0 0",
              color: "#0E141B",
              letterSpacing: "-0.02em",
            }}
          >
            {promo.title}
          </h2>
          <p
            style={{
              fontSize: "0.86rem",
              color: "#3A434E",
              lineHeight: 1.7,
              margin: "10px 0 0",
            }}
          >
            {promo.description}
          </p>
        </div>
        <div style={{ padding: "20px 28px 16px" }}>
          <button
            type="button"
            onClick={() => {
              promo.onButtonClick(nickname);
              hideToday();
            }}
            style={{
              width: "100%",
              border: "none",
              borderRadius: "13px",
              padding: "15px",
              background: "#00A396",
              color: "#fff",
              fontWeight: 800,
              fontSize: "1rem",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {promo.buttonText}
          </button>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "14px",
              padding: "0 2px",
            }}
          >
            <button
              type="button"
              onClick={hideToday}
              style={{
                border: "none",
                background: "none",
                color: "#8a949c",
                fontSize: "0.82rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                padding: 0,
              }}
            >
              오늘 하루 보지 않기
            </button>
            <button
              type="button"
              onClick={close}
              style={{
                border: "none",
                background: "none",
                color: "#3A434E",
                fontSize: "0.82rem",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                padding: 0,
              }}
            >
              닫기 ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperiencePromoModal;
